import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ActvScopService } from "app/services/cp/ActvScopService";
import { useTranslation } from "react-i18next";
import { EmissionService } from "app/services/cp/EmissionService";
import {parseNumberWithComma, roundWithComma} from "app/utils/stringUtil";
import {ExistCpDataConstant} from "app/constants/cpConstant";
import DashboardNoData from "./DashboardNoData";
import {ResponseResultCode} from "app/model/response-api";
import DashboardLoading from "./DashboardLoading";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";
import GhgsEmissQntPopper from "app/pages/db/modules/popup/GhgsEmissQntPopper";

export default function GhgsEmissQnt(props: Readonly<DashboardModuleProps>) {
  const [scopUse, setScopUse] = useState<any>();
  const { t } = useTranslation();
  const toDayYear = new Date().getFullYear();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [resultOne, setResultOne] = useState<any>({
    scope1: { scopeNm: "" },
    scope2: { scopeNm: "" },
    scope3: { scopeNm: "" },
  });

  const [existBoundarySetting, setExistBoundarySetting] = useState<any>(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        const response = await ActvScopService.getInstance().existCpData([ExistCpDataConstant.S]);
        if (response && response.resultCode === "Y") {
          if (response.data?.SETTING) {
            setExistBoundarySetting(true);
            await getData();
          }
        }
      } catch (e) {
        toast.error("시스템 오류가 발생하였습니다.");
      } finally {
        setIsLoading(false);
      }
    };
    initialize();
  }, []);

  const getData = async () => {
    ActvScopService.getInstance().getCheckScope().then((response: any) => {
      if (response && response.resultCode === ResponseResultCode.Y && response.data) {
        setScopUse(response.data);
      }
    }).catch(() => {
      toast.error(t('common.message.error.system'));
    });

    EmissionService.getInstance()
    .getEmissiopnStatus(toDayYear.toString())
    .then(function (res: any) {
      if(!res?.data) {
        return
      }
      const resultMap = res.data.reduce(
          (acc: any, item: any) => {
            let {
              scopId,
              mm1EmsnQnt,
              mm2EmsnQnt,
              mm3EmsnQnt,
              mm4EmsnQnt,
              mm5EmsnQnt,
              mm6EmsnQnt,
              mm7EmsnQnt,
              mm8EmsnQnt,
              mm9EmsnQnt,
              mm10EmsnQnt,
              mm11EmsnQnt,
              mm12EmsnQnt,
            } = item;

            if (scopId === "연료사용" || scopId === "폐기물소각" || scopId === "공정배출") {
              scopId = "scope1";
            } else if (scopId === "전력 사용시설" || scopId === "열/스팀 사용시설") {
              scopId = "scope2";
            } else {
              scopId = "scope3";
            }

            acc[scopId].mm1EmsnQnt = (acc[scopId].mm1EmsnQnt + mm1EmsnQnt);
            acc[scopId].mm2EmsnQnt = (acc[scopId].mm2EmsnQnt + mm2EmsnQnt);
            acc[scopId].mm3EmsnQnt = (acc[scopId].mm3EmsnQnt + mm3EmsnQnt);
            acc[scopId].mm4EmsnQnt = (acc[scopId].mm4EmsnQnt + mm4EmsnQnt);
            acc[scopId].mm5EmsnQnt = (acc[scopId].mm5EmsnQnt + mm5EmsnQnt);
            acc[scopId].mm6EmsnQnt = (acc[scopId].mm6EmsnQnt + mm6EmsnQnt);
            acc[scopId].mm7EmsnQnt = (acc[scopId].mm7EmsnQnt + mm7EmsnQnt);
            acc[scopId].mm8EmsnQnt = (acc[scopId].mm8EmsnQnt + mm8EmsnQnt);
            acc[scopId].mm9EmsnQnt = (acc[scopId].mm9EmsnQnt + mm9EmsnQnt);
            acc[scopId].mm10EmsnQnt = (acc[scopId].mm10EmsnQnt + mm10EmsnQnt);
            acc[scopId].mm11EmsnQnt = (acc[scopId].mm11EmsnQnt + mm11EmsnQnt);
            acc[scopId].mm12EmsnQnt = (acc[scopId].mm12EmsnQnt + mm12EmsnQnt);
            acc[scopId].total = (acc[scopId].total +
                mm1EmsnQnt +
                mm2EmsnQnt +
                mm3EmsnQnt +
                mm4EmsnQnt +
                mm5EmsnQnt +
                mm6EmsnQnt +
                mm7EmsnQnt +
                mm8EmsnQnt +
                mm9EmsnQnt +
                mm10EmsnQnt +
                mm11EmsnQnt +
                mm12EmsnQnt);

            return acc;
          },
          {
            scope1: {
              scopeNm: "scope1",
              total: 0,
            },
            scope2: {
              scopeNm: "scope2",
              total: 0,
            },
            scope3: {
              scopeNm: "scope3",
              total: 0,
            },
          }
      );
      Object.keys(resultMap).forEach((key: string) => {
        resultMap[key].total = parseFloat(resultMap[key].total.toFixed(2));
      });
      setResultOne(resultMap);
    }).catch(() => {
      toast.error(t("common.message.error.system"));
    });
  }

  const [scope1Percent, setScope1Percent] = useState<any>(0);
  const [scope2Percent, setScope2Percent] = useState<any>(0);
  const [scope3Percent, setScope3Percent] = useState<any>(0);

  function calculateNumber(value: any): number {
    if (value === undefined || value === null || isNaN(Number(value))) {
      return 0;
    }
    return Number(value); // 숫자로 변환 후 반환
  }
  useEffect(() => {
    const scope1Total = calculateNumber(resultOne.scope1.total)
    const scope2Total = calculateNumber(resultOne.scope2.total)
    const scope3Total = calculateNumber(resultOne.scope3.total)
    const totalSum = scope1Total + scope2Total + scope3Total;

    setScope1Percent(calculateNumber((scope1Total / totalSum) * 100).toFixed(2));
    setScope2Percent(calculateNumber((scope2Total / totalSum) * 100).toFixed(2));
    setScope3Percent(calculateNumber((scope3Total / totalSum) * 100).toFixed(2));
  }, [resultOne]);

  return (
    <DashboardCard
      title="온실가스 배출량"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/pc/gas/scope"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      {!existBoundarySetting ? (
        <DashboardNoData
          message="탄소플래너 운영경계를 설정하세요."
          link={{text: '경계 설정하기', to: '/cp/inventory'}}
        />
      ) : (
        <div className="sub-inner-flex1">
          <div className="total-txt-box">
            <p>
              <span>총 배출량</span>
              <strong>{roundWithComma(calculateNumber(resultOne.scope1.total) + calculateNumber(resultOne.scope2.total) + calculateNumber(resultOne.scope3.total), 2)}</strong>
              <em>tCO₂eq</em>
            </p>
          </div>
          <div className="percent-row-graph">
            <div className="tit-legend">
              <div className="legend">
                <span><em className="c1"></em>Scope1</span>
                <span><em className="c2"></em>Scope2</span>
                <span><em className="c3"></em>Scope3</span>
              </div>
            </div>
            <GhgsEmissQntPopper percent1={scope1Percent} percent2={scope2Percent} percent3={scope3Percent} />
          </div>
          <div className="row-col-gap2">
            <div className="box-solo">
              <div className="inner">
                <p>Scope1</p>
                <strong className="c-num">
                  <em data-count={resultOne.scope1.total} className="change">
                    {parseNumberWithComma(resultOne.scope1.total)}
                  </em>tCO₂eq
                </strong>
              </div>
            </div>
            <div className="box-solo">
              <div className="inner">
                <p>Scope2</p>
                <strong className="c-num">
                  <em data-count={resultOne.scope2.total} className="change">
                    {parseNumberWithComma(resultOne.scope2.total)}
                  </em>tCO₂eq
                </strong>
              </div>
            </div>
            <div className="box-solo">
              <div className="inner">
                <p>Scope3</p>
                <strong className="c-num">
                  <em data-count={resultOne.scope3.total} className="change">
                    {parseNumberWithComma(resultOne.scope3.total)}
                  </em>tCO₂eq
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardCard>
  )
}
